/**
 * Insights store — Context provider for tenant memory (B3.0).
 *
 * Two catalogues, single provider:
 *   • Tenant insights        (`divineiot:insights`)
 *   • Intervention log       (`divineiot:interventions`)
 *
 * Auto-derived insights carry `source: "auto"` and a sourceDatasetId
 * pointer; when the underlying dataset is removed, the matching
 * insights are also dropped (cascade behaviour wired in B2.3a's
 * datasets.remove path; B3.0 just exposes the hook).
 *
 * Mirrors the Reports / Imports / Attachments provider shape so the
 * mental model stays uniform across the codebase. Phase-1 swap:
 * replace localStorage with `/v1/twin/insights` + per-tenant DB
 * predicates (the privacy boundary in CIE § 5).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  InterventionLogEntry,
  TenantInsight,
  InsightKind,
} from "./types";
import {
  isLiveApi,
  insights as insightsApi,
  interventions as interventionsApi,
  toTenantInsight,
  toInterventionEntry,
} from "@/lib/api";
import { useOrg } from "@/lib/org-context";

const INSIGHTS_KEY = "divineiot:insights";
const INTERVENTIONS_KEY = "divineiot:interventions";

// ────────────────────────────────────────────────────────────────────
// State shape
// ────────────────────────────────────────────────────────────────────

export interface InsightsState {
  insights: TenantInsight[];
  interventions: InterventionLogEntry[];

  byId: (id: string) => TenantInsight | undefined;
  byKind: (kind: InsightKind) => TenantInsight[];
  byDataset: (datasetId: string) => TenantInsight[];

  /** Insert a new insight. Returns its id. */
  upsertInsight: (
    insight: Omit<TenantInsight, "id" | "observedAt"> & {
      id?: string;
      observedAt?: string;
    },
  ) => string;

  /** Replace an entire bag at once (used by auto-derivation when a
   *  dataset re-computes — drop the dataset's old insights + insert
   *  the fresh batch atomically). */
  replaceForDataset: (
    datasetId: string,
    nextBatch: Array<Omit<TenantInsight, "id" | "observedAt"> & { id?: string }>,
  ) => void;

  /** Mark an insight as user-validated. */
  validate: (id: string) => void;

  /** Remove a single insight. */
  removeInsight: (id: string) => void;

  /** Remove every insight tied to a dataset (called from datasets.remove). */
  removeForDataset: (datasetId: string) => void;

  /** Clear all (admin). */
  clear: () => void;

  /** Intervention log mutations. */
  addIntervention: (
    entry: Omit<InterventionLogEntry, "id" | "startedAt"> & {
      id?: string;
      startedAt?: string;
    },
  ) => string;
  updateIntervention: (id: string, patch: Partial<InterventionLogEntry>) => void;
  removeIntervention: (id: string) => void;
  /** Read the intervention log again from the platform (after an import of the plant's log, B7 T3). */
  reloadInterventions: () => void;

  /** Mutation counter for memo consumers. */
  version: number;
}

// ────────────────────────────────────────────────────────────────────
// Context
// ────────────────────────────────────────────────────────────────────

const InsightsContext = createContext<InsightsState | null>(null);

export function useInsights(): InsightsState {
  const ctx = useContext(InsightsContext);
  if (!ctx) {
    throw new Error("useInsights must be used inside <InsightsProvider>");
  }
  return ctx;
}

export function useOptionalInsights(): InsightsState | null {
  return useContext(InsightsContext);
}

// ────────────────────────────────────────────────────────────────────
// Persistence
// ────────────────────────────────────────────────────────────────────

function readInsights(): TenantInsight[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(INSIGHTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TenantInsight[]) : [];
  } catch {
    return [];
  }
}

function writeInsights(items: TenantInsight[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(INSIGHTS_KEY, JSON.stringify(items));
  } catch {
    /* swallow */
  }
}

function readInterventions(): InterventionLogEntry[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(INTERVENTIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as InterventionLogEntry[]) : [];
  } catch {
    return [];
  }
}

function writeInterventions(items: InterventionLogEntry[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(INTERVENTIONS_KEY, JSON.stringify(items));
  } catch {
    /* swallow */
  }
}

// ────────────────────────────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────────────────────────────

export function InsightsProvider({ children }: { children: ReactNode }) {
  const [insights, setInsights] = useState<TenantInsight[]>(() =>
    isLiveApi ? [] : readInsights(),
  );
  const [interventions, setInterventions] = useState<InterventionLogEntry[]>(() =>
    isLiveApi ? [] : readInterventions(),
  );
  const [version, setVersion] = useState(0);
  const { activeOrgId } = useOrg();

  // Live mode: hydrate both catalogues from the durable backend for the ACTIVE
  // org. Keyed on activeOrgId so it re-runs on an org switch: clear the previous
  // org's tenant memory FIRST so org A's insights + intervention log never
  // linger under org B (and a mutation can't persist under the wrong org) —
  // #92. On failure we keep the current (post-reset) state rather than blanking.
  useEffect(() => {
    if (!isLiveApi) return;
    setInsights([]);
    setInterventions([]);
    if (!activeOrgId) return;
    let cancelled = false;
    Promise.all([insightsApi.list(), interventionsApi.list()])
      .then(([ins, ints]) => {
        if (cancelled) return;
        setInsights((ins.items ?? []).map(toTenantInsight));
        setInterventions((ints.items ?? []).map(toInterventionEntry));
      })
      .catch(() => {
        /* keep current */
      });
    return () => {
      cancelled = true;
    };
  }, [activeOrgId]);

  // Stub mode only: mirror to localStorage. Live mode is server-backed via
  // /v1/twin/insights + /v1/twin/interventions (the mutations below persist).
  useEffect(() => {
    if (!isLiveApi) writeInsights(insights);
  }, [insights]);
  useEffect(() => {
    if (!isLiveApi) writeInterventions(interventions);
  }, [interventions]);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const byId = useCallback(
    (id: string) => insights.find((i) => i.id === id),
    [insights],
  );

  const byKind = useCallback(
    (kind: InsightKind) => insights.filter((i) => i.kind === kind),
    [insights],
  );

  const byDataset = useCallback(
    (datasetId: string) =>
      insights.filter((i) => {
        const p = i.payload as { sourceDatasetId?: string };
        return p.sourceDatasetId === datasetId;
      }),
    [insights],
  );

  const upsertInsight = useCallback<InsightsState["upsertInsight"]>(
    (seed) => {
      const id = seed.id ?? `ins-${Date.now()}-${Math.floor(Math.random() * 10_000)}`;
      const observedAt = seed.observedAt ?? new Date().toISOString();
      const next: TenantInsight = {
        id,
        kind: seed.kind,
        tier: seed.tier,
        source: seed.source,
        confidence: seed.confidence,
        observedAt,
        validatedAt: seed.validatedAt,
        payload: seed.payload,
      };
      setInsights((prev) => {
        const existingIdx = prev.findIndex((i) => i.id === id);
        if (existingIdx >= 0) {
          const copy = [...prev];
          copy[existingIdx] = next;
          return copy;
        }
        return [next, ...prev];
      });
      bump();
      if (isLiveApi) {
        void insightsApi.upsert({ id, kind: next.kind, data: next }).catch(() => {});
      }
      return id;
    },
    [bump],
  );

  const replaceForDataset = useCallback<InsightsState["replaceForDataset"]>(
    (datasetId, nextBatch) => {
      const mapped: TenantInsight[] = nextBatch.map((seed) => ({
        id: seed.id ?? `ins-${Date.now()}-${Math.floor(Math.random() * 100_000)}`,
        kind: seed.kind,
        tier: seed.tier,
        source: seed.source,
        confidence: seed.confidence,
        observedAt: new Date().toISOString(),
        validatedAt: seed.validatedAt,
        payload: seed.payload,
      }));
      setInsights((prev) => [
        ...mapped,
        ...prev.filter((i) => {
          const p = i.payload as { sourceDatasetId?: string };
          return p.sourceDatasetId !== datasetId;
        }),
      ]);
      bump();
      if (isLiveApi) {
        // Drop the dataset's old insights, then upsert the fresh batch.
        insights
          .filter(
            (i) =>
              (i.payload as { sourceDatasetId?: string }).sourceDatasetId === datasetId,
          )
          .forEach((i) => void insightsApi.remove(i.id).catch(() => {}));
        mapped.forEach(
          (i) => void insightsApi.upsert({ id: i.id, kind: i.kind, data: i }).catch(() => {}),
        );
      }
    },
    [insights, bump],
  );

  const validate = useCallback<InsightsState["validate"]>(
    (id) => {
      const now = new Date().toISOString();
      const target = insights.find((i) => i.id === id);
      setInsights((prev) =>
        prev.map((i) => (i.id === id ? { ...i, validatedAt: now } : i)),
      );
      bump();
      if (isLiveApi && target) {
        const next = { ...target, validatedAt: now };
        void insightsApi.upsert({ id, kind: next.kind, data: next }).catch(() => {});
      }
    },
    [insights, bump],
  );

  const removeInsight = useCallback<InsightsState["removeInsight"]>(
    (id) => {
      setInsights((prev) => prev.filter((i) => i.id !== id));
      bump();
      if (isLiveApi) void insightsApi.remove(id).catch(() => {});
    },
    [bump],
  );

  const removeForDataset = useCallback<InsightsState["removeForDataset"]>(
    (datasetId) => {
      const removed = insights.filter(
        (i) => (i.payload as { sourceDatasetId?: string }).sourceDatasetId === datasetId,
      );
      setInsights((prev) =>
        prev.filter((i) => {
          const p = i.payload as { sourceDatasetId?: string };
          return p.sourceDatasetId !== datasetId;
        }),
      );
      bump();
      if (isLiveApi) {
        removed.forEach((i) => void insightsApi.remove(i.id).catch(() => {}));
      }
    },
    [insights, bump],
  );

  const clear = useCallback(() => {
    const oldInsights = insights;
    const oldInterventions = interventions;
    setInsights([]);
    setInterventions([]);
    bump();
    if (isLiveApi) {
      oldInsights.forEach((i) => void insightsApi.remove(i.id).catch(() => {}));
      oldInterventions.forEach((e) => void interventionsApi.remove(e.id).catch(() => {}));
    }
  }, [insights, interventions, bump]);

  const addIntervention = useCallback<InsightsState["addIntervention"]>(
    (seed) => {
      const id =
        seed.id ?? `int-${Date.now()}-${Math.floor(Math.random() * 10_000)}`;
      const next: InterventionLogEntry = {
        id,
        problem: seed.problem,
        intervention: seed.intervention,
        outcome: seed.outcome,
        notes: seed.notes,
        relatedInsightId: seed.relatedInsightId,
        startedAt: seed.startedAt ?? new Date().toISOString(),
        endedAt: seed.endedAt,
      };
      setInterventions((prev) => [next, ...prev]);
      bump();
      if (isLiveApi) {
        void interventionsApi.upsert({ id, data: next }).catch(() => {});
      }
      return id;
    },
    [bump],
  );

  const updateIntervention = useCallback<InsightsState["updateIntervention"]>(
    (id, patch) => {
      const target = interventions.find((e) => e.id === id);
      setInterventions((prev) =>
        prev.map((e) => (e.id === id ? { ...e, ...patch } : e)),
      );
      bump();
      if (isLiveApi && target) {
        const next = { ...target, ...patch };
        void interventionsApi.upsert({ id, data: next }).catch(() => {});
      }
    },
    [interventions, bump],
  );

  const removeIntervention = useCallback<InsightsState["removeIntervention"]>(
    (id) => {
      setInterventions((prev) => prev.filter((e) => e.id !== id));
      bump();
      if (isLiveApi) void interventionsApi.remove(id).catch(() => {});
    },
    [bump],
  );

  // The log read again from the platform, as the hydrate above reads it (live mode only): an import of the plant's own
  // log (B7 T3) writes entries the page did not make.
  const reloadInterventions = useCallback<InsightsState["reloadInterventions"]>(() => {
    if (!isLiveApi) return;
    void interventionsApi
      .list()
      .then((ints) => {
        setInterventions((ints.items ?? []).map(toInterventionEntry));
        bump();
      })
      .catch(() => {});
  }, [bump]);

  const value = useMemo<InsightsState>(
    () => ({
      insights,
      interventions,
      byId,
      byKind,
      byDataset,
      upsertInsight,
      replaceForDataset,
      validate,
      removeInsight,
      removeForDataset,
      clear,
      addIntervention,
      updateIntervention,
      removeIntervention,
      reloadInterventions,
      version,
    }),
    [
      insights,
      interventions,
      byId,
      byKind,
      byDataset,
      upsertInsight,
      replaceForDataset,
      validate,
      removeInsight,
      removeForDataset,
      clear,
      addIntervention,
      updateIntervention,
      removeIntervention,
      reloadInterventions,
      version,
    ],
  );

  return (
    <InsightsContext.Provider value={value}>{children}</InsightsContext.Provider>
  );
}
